import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { AlertCircle, Save, User, X } from 'lucide-react';
import { SignatureCanvas } from './SignatureCanvas';
import retiroCopiasService from '../../services/retiroCopiasService';
import type {
  RetiroCopia,
  RetiradorErrors,
  UpdateRetiradorPayload,
} from '../../types';

interface EditRetiroDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  retiro: RetiroCopia;
  otroTramiteId: string;
  copiasDisponibles: number; // Copias disponibles del trámite (sin contar las del retiro actual)
}

interface EditableRetirador {
  id?: string;
  nombreCompleto: string;
  cantidadCopias: number;
  firmaDigital: Blob | null;
  firmaDigitalUrl?: string;
  keepFirmaDigital: boolean;
}

export const EditRetiroDialog: React.FC<EditRetiroDialogProps> = ({
  open,
  onOpenChange,
  retiro,
  otroTramiteId,
  copiasDisponibles,
}) => {
  const queryClient = useQueryClient();

  // State
  const [fechaRetiro, setFechaRetiro] = useState(retiro.fechaRetiro);
  const [retiradores, setRetiradores] = useState<EditableRetirador[]>([]);
  const [errors, setErrors] = useState<RetiradorErrors[]>([]);
  const [generalError, setGeneralError] = useState<string | null>(null);

  // Initialize retiradores from retiro data
  useEffect(() => {
    if (open) {
      const initialRetiradores: EditableRetirador[] = retiro.retiradores.map((r) => ({
        id: r.id,
        nombreCompleto: r.nombreCompleto,
        cantidadCopias: r.cantidadCopias,
        firmaDigital: null,
        firmaDigitalUrl: r.firmaDigitalUrl,
        keepFirmaDigital: true,
      }));
      setRetiradores(initialRetiradores);
      setFechaRetiro(retiro.fechaRetiro);
      setErrors([]);
      setGeneralError(null);
    }
  }, [open, retiro]);

  // Mutation
  const updateMutation = useMutation({
    mutationFn: async () => {
      const updatePayload: UpdateRetiradorPayload[] = retiradores.map((r) => ({
        id: r.id,
        nombreCompleto: r.nombreCompleto,
        cantidadCopias: r.cantidadCopias,
        firmaDigital: r.firmaDigital || undefined,
        keepFirmaDigital: r.keepFirmaDigital && !r.firmaDigital,
      }));

      return retiroCopiasService.updateRetiro({
        retiroId: retiro.id,
        fechaRetiro,
        retiradores: updatePayload,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['retiros', otroTramiteId] });
      queryClient.invalidateQueries({ queryKey: ['otros-tramites'] });
      onOpenChange(false);
      // TODO: Mostrar toast de éxito
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Error al actualizar el retiro';
      setGeneralError(message);
    },
  });

  // Validations
  const validateRetirador = (retirador: EditableRetirador): RetiradorErrors => {
    const errors: RetiradorErrors = {};

    if (!retirador.nombreCompleto || retirador.nombreCompleto.trim().length < 3) {
      errors.nombreCompleto = 'El nombre completo debe tener al menos 3 caracteres';
    }

    if (!retirador.cantidadCopias || retirador.cantidadCopias < 1) {
      errors.cantidadCopias = 'Debe seleccionar al menos 1 copia';
    }

    const hasFirmaFile = retirador.firmaDigital || retirador.keepFirmaDigital;

    if (!hasFirmaFile) {
      errors.firmaDigital = 'La firma digital es obligatoria';
    }

    return errors;
  };

  const validate = (): boolean => {
    const newErrors: RetiradorErrors[] = retiradores.map(validateRetirador);
    setErrors(newErrors);

    const hasErrors = newErrors.some(e => Object.keys(e).length > 0);
    if (hasErrors) {
      setGeneralError('Por favor complete todos los campos obligatorios');
      return false;
    }

    // Validate total copies doesn't exceed available
    const totalCopias = retiradores.reduce((sum, r) => sum + r.cantidadCopias, 0);
    const maxAllowed = copiasDisponibles + retiro.cantidadTotalRetirada; // Disponibles + las que ya estaban retiradas

    if (totalCopias > maxAllowed) {
      setGeneralError(`El total de copias (${totalCopias}) excede las disponibles (${maxAllowed})`);
      return false;
    }

    setGeneralError(null);
    return true;
  };

  // Handlers
  const handleSubmit = async () => {
    if (validate()) {
      await updateMutation.mutateAsync();
    }
  };

  const updateRetirador = (index: number, updates: Partial<EditableRetirador>) => {
    const newRetiradores = [...retiradores];
    newRetiradores[index] = { ...newRetiradores[index], ...updates };
    setRetiradores(newRetiradores);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
      <DialogContent className="max-w-4xl max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Save className="h-5 w-5" />
            Editar Retiro
          </DialogTitle>
          <DialogDescription>
            Modifique los datos del retiro de copias
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-6">
          {/* Fecha de Retiro */}
          <div className="space-y-2">
            <Label htmlFor="fecha-retiro">
              Fecha de Retiro
              <span className="text-red-500 ml-1">*</span>
            </Label>
            <Input
              id="fecha-retiro"
              type="date"
              value={fechaRetiro}
              onChange={(e) => setFechaRetiro(e.target.value)}
              disabled={updateMutation.isPending}
            />
          </div>

          {/* Retiradores */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700">
              Personas que Retiran ({retiradores.length})
            </h3>

            {retiradores.map((retirador, index) => (
              <div
                key={retirador.id || index}
                className="border border-gray-200 rounded-lg p-4 space-y-4 bg-gray-50"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-gray-500" />
                    <h4 className="text-sm font-medium text-gray-900">
                      Persona {index + 1}
                    </h4>
                  </div>
                </div>

                {/* Nombre Completo */}
                <div className="space-y-2">
                  <Label htmlFor={`nombre-${index}`}>
                    Nombre Completo
                    <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <Input
                    id={`nombre-${index}`}
                    type="text"
                    value={retirador.nombreCompleto}
                    onChange={(e) => updateRetirador(index, { nombreCompleto: e.target.value })}
                    placeholder="Nombre completo de quien retira"
                    disabled={updateMutation.isPending}
                    className={errors[index]?.nombreCompleto ? 'border-red-300' : ''}
                  />
                  {errors[index]?.nombreCompleto && (
                    <p className="text-sm text-red-600">{errors[index].nombreCompleto}</p>
                  )}
                </div>

                {/* Cantidad de Copias */}
                <div className="space-y-2">
                  <Label htmlFor={`cantidad-${index}`}>
                    Cantidad de Copias
                    <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <Input
                    id={`cantidad-${index}`}
                    type="number"
                    min={1}
                    value={retirador.cantidadCopias}
                    onChange={(e) => {
                      const value = Number.parseInt(e.target.value, 10);
                      if (!Number.isNaN(value) && value >= 1) {
                        updateRetirador(index, { cantidadCopias: value });
                      }
                    }}
                    disabled={updateMutation.isPending}
                    className={errors[index]?.cantidadCopias ? 'border-red-300' : ''}
                  />
                  {errors[index]?.cantidadCopias && (
                    <p className="text-sm text-red-600">{errors[index].cantidadCopias}</p>
                  )}
                </div>

                {/* Firma Digital */}
                <div>
                  <SignatureCanvas
                    label="Firma Digital"
                    onSave={(blob) => {
                      updateRetirador(index, {
                        firmaDigital: blob,
                        keepFirmaDigital: blob ? false : retirador.keepFirmaDigital,
                      });
                    }}
                    disabled={updateMutation.isPending}
                    error={errors[index]?.firmaDigital}
                    existingSignatureUrl={retirador.firmaDigitalUrl}
                  />
                  {!retirador.firmaDigital && retirador.firmaDigitalUrl && (
                    <div className="mt-2 text-xs text-gray-600">
                      Se mantendrá la firma actual si no firma nuevamente
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Info sobre copias disponibles */}
          <div className="rounded-lg bg-blue-50 border border-blue-200 p-4">
            <p className="text-sm text-blue-900">
              <span className="font-semibold">Total de copias editadas:</span>{' '}
              {retiradores.reduce((sum, r) => sum + r.cantidadCopias, 0)}
            </p>
            <p className="text-xs text-blue-700 mt-1">
              Máximo permitido: {copiasDisponibles + retiro.cantidadTotalRetirada} copias
            </p>
          </div>
        </div>

        {/* General error */}
        {generalError && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{generalError}</p>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={updateMutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            loading={updateMutation.isPending}
            disabled={updateMutation.isPending}
          >
            <Save className="h-4 w-4 mr-2" />
            Guardar Cambios
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
